import React, { useState, useEffect } from 'react';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';
import app from 'new-ui/app';
import {
  LOGIN_TOKEN_URL_SEARCH_PARAM,
  START_SIGNUP_URL_SEARCH_PARAM,
  USER_TYPE_URL_SEARCH_PARAM,
  ACCESS_TOKEN_URL_SEARCH_PARAM,
  REFRESH_TOKEN_URL_SEARCH_PARAM,
  AUTH_TYPE_URL_SEARCH_PARAM,
  REDIRECT_URL_SEARCH_PARAM,
  NEW_PROFILE_URL_SEARCH_PARAM,
} from 'utils/constants/urlSearchParameters';
import useRecaptcha from 'utils/hooks/useRecaptcha';
import { useLocationSearchParameters } from 'utils/hooks/useLocationSearchParameters';
import { useTranslation } from 'utils/modifiedTranslation';
import { authenticationActions } from 'components/Authentication/actions';
import { AUTH_TYPE } from 'utils/constants/AuthenticationType';
import { MATCH_FLOWS } from 'modules/matches/constants';
import { useHCPUserType } from 'modules/hcp/useHCPUserType';
import { ROUTE_NAME, ROUTE_PATH } from 'utils/constants/router';
import { preserveQueryParam } from 'functions/preserveQueryParam';
import { assembleRoutePath } from 'functions/assembleRoutePath';
import { useGoogleOneTapAuth } from 'utils/hooks/useGoogleOneTapAuth';
import { useUpdateLocationSearchParameters } from 'utils/hooks/useUpdateLocationSearchParameters';
import { GoogleOneTapAuth } from 'components/GoogleOneTapAuth/GoogleOneTapAuth';
import { GA_HCP } from 'modules/ga/hcp';
import { useSetHeaderSettings } from 'components/PageHeader/PageHeaderContext';
import { HEADER_THEME, VERSIONS } from 'components/PageHeader/constants';
import { OTPLogin } from './components/OTPLogin';
import { EmailLogin } from './components/EmailLogin';
import PhoneIcon from './components/icons/phone.svg';
import EmailIcon from './components/icons/email.svg';
import './login.css';

const LOGIN_ICONS = {
  [AUTH_TYPE.EMAIL]: {
    icon: PhoneIcon,
    title: 'phone',
  },
  [AUTH_TYPE.OTP]: {
    icon: EmailIcon,
    title: 'email',
  },
};

export const Login = () => {
  const dispatch = useDispatch();
  const {
    [REDIRECT_URL_SEARCH_PARAM]: redirectSearchParam,
    [AUTH_TYPE_URL_SEARCH_PARAM]: typeSearchParam,
  } = useLocationSearchParameters();
  const replaceLocationSearchParameters = useUpdateLocationSearchParameters({ isPushMethod: false });
  const { t } = useTranslation();
  const [loginType, setLoginType] = useState(typeSearchParam === AUTH_TYPE.EMAIL ? AUTH_TYPE.EMAIL : AUTH_TYPE.OTP);
  const { recaptcha, getRecaptchaTokenAsync } = useRecaptcha();

  const userType = useHCPUserType();

  useSetHeaderSettings({
    version: VERSIONS.DEFAULT,
    theme: HEADER_THEME.WHITE,
  });

  const googleOneTapSignInCallback = async (tokenId) => {
    dispatch(
      authenticationActions.requestLogin(
        {
          info: {
            externalAuthParams: {
              tokenId,
            },
          },
          recaptchaToken: await getRecaptchaTokenAsync(),
        },
        AUTH_TYPE.GOOGLE,
      ),
    );
  };

  useGoogleOneTapAuth(googleOneTapSignInCallback);

  const { icon: loginIcon, title: iconTitle } = LOGIN_ICONS[loginType];

  useEffect(() => {
    app.intercom.shutdown();
    const {
      [ACCESS_TOKEN_URL_SEARCH_PARAM]: tj_access_token,
      [REFRESH_TOKEN_URL_SEARCH_PARAM]: tj_refresh_token,
      [LOGIN_TOKEN_URL_SEARCH_PARAM]: token,
    } = queryString.parse(window.location.search);

    const silentLogin = async () => {
      const recaptchaToken = await getRecaptchaTokenAsync();
      dispatch(authenticationActions.requestLogin({
        token,
        recaptchaToken,
      }, AUTH_TYPE.MAGIC_LINK));
    };

    if (token) {
      silentLogin();
    } else if (tj_access_token && tj_refresh_token) {
      localStorage.clear();

      authenticationActions.setTokens(tj_access_token, tj_refresh_token);
      authenticationActions.setSuperUser();
      dispatch(authenticationActions.finishLogin({
        matchFlow: MATCH_FLOWS.LATEST,
        navigationPath: ROUTE_PATH[ROUTE_NAME.RESULTS],
      }));
    }
  }, [dispatch, getRecaptchaTokenAsync]);

  const loginButtonClick = () => {
    const nextLoginType = loginType === AUTH_TYPE.EMAIL ? AUTH_TYPE.OTP : AUTH_TYPE.EMAIL;
    setLoginType(nextLoginType);
    replaceLocationSearchParameters({ [AUTH_TYPE_URL_SEARCH_PARAM]: nextLoginType });
  };

  const loginButtonTitle = () => {
    if (loginType === AUTH_TYPE.EMAIL) return t('login.login_with_one_time_code');
    return t('login.login_with_email_password');
  };

  const gotoSignup = () => {
    if (redirectSearchParam === ROUTE_PATH[ROUTE_NAME.FINANCE]) {
      window.location = `${ROUTE_PATH[ROUTE_NAME.FINANCE]}?create=1`;
      return;
    }
    if (redirectSearchParam === ROUTE_PATH[ROUTE_NAME.SEARCH]) {
      app.sendGoogleAnalyticsEvent(GA_HCP.category, GA_HCP.events.SIGNUP_CLICKED);

      dispatch(push(assembleRoutePath({
        pathname: ROUTE_PATH[ROUTE_NAME.SEARCH],
        searchParameters: { [USER_TYPE_URL_SEARCH_PARAM]: userType, [START_SIGNUP_URL_SEARCH_PARAM]: 1 },
      })));
      return;
    }

    dispatch(push(preserveQueryParam(ROUTE_PATH[ROUTE_NAME.HOME], { extra: [[NEW_PROFILE_URL_SEARCH_PARAM, '1']] })));
  };

  return (
    <div className="login" id="login">
      <GoogleOneTapAuth />
      {recaptcha}
      <div className="login-container">
        <div className="login-box">
          {loginType === AUTH_TYPE.EMAIL ? <EmailLogin /> : <OTPLogin />}
        </div>
        <div className="login-options">
          <div className="title">{t('login.or_continue_with')}</div>
          <div className="login-buttons">
            <div
              className="login-button"
              onClick={loginButtonClick}
            >
              <img className="icon" src={loginIcon} alt={iconTitle} />
              {
                loginButtonTitle()
              }
            </div>
          </div>
          <div className="no-account">
            {t('login.dont_have_an_account')}
            {' '}
            <span className="inline-button" onClick={gotoSignup}>{t('login.form.footer.signup')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
